import { useEffect, useRef, useMemo } from 'react';

/**
 * Tracks whether form state has changed from its initial value.
 * Also registers a `beforeunload` handler when dirty so the browser
 * prompts if the user tries to close/refresh the tab.
 */
export function useUnsavedChanges<T>(initialState: T, currentState: T): boolean {
  const initialSnapshot = useRef(JSON.stringify(initialState));

  // Update the snapshot when the initial state identity changes
  // (e.g. when server data loads in edit page)
  const prevInitial = useRef(initialState);
  if (prevInitial.current !== initialState) {
    prevInitial.current = initialState;
    initialSnapshot.current = JSON.stringify(initialState);
  }

  const isDirty = useMemo(
    () => JSON.stringify(currentState) !== initialSnapshot.current,
    [currentState]
  );

  useEffect(() => {
    if (!isDirty) return;

    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return isDirty;
}
